// src/pages/SMiddlePageTutor.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { gql, useApolloClient, useQuery } from "@apollo/client";
import { Link, useParams } from "react-router-dom";
import "./../styles/SMiddlePage.css";
import "./../styles/LessonContentTypography.css";
import { installSavedFormulaRenderer } from "../utils/renderSavedFormulas";

const DEFAULT_FACULTY_ID = "1c4e71de-9018-44d3-929e-042704d86516";

const SUBJECTS_QUERY3 = gql`
  query Subjects {
    subjects {
      id
      title
      slug
      description
      isPublic
    }
  }
`;

const FACULTY_CATEGORIES_QUERY3 = gql`
  query FacultyCategoriesByFaculty($facultyId: ID!) {
    facultyCategoriesByFaculty(facultyId: $facultyId) {
      id
      name
      position
    }
  }
`;

const SUBJECTS_BY_CATEGORY_QUERY3 = gql`
  query SubjectsByFacultyCategory($facultyCategoryId: ID!) {
    subjectsByFacultyCategory(facultyCategoryId: $facultyCategoryId) {
      position
      subject {
        id
        slug
      }
      facultyCategory {
        id
      }
    }
  }
`;

const SUBJECT_DETAIL_BLOCKS_QUERY3 = gql`
  query SubjectDetailBlocks($subjectId: ID!) {
    subjectDetailBlocks(subjectId: $subjectId) {
      id
      subjectId
      position
      html
    }
  }
`;

const LESSON_CATEGORIES_BY_SUBJECT_QUERY3 = gql`
  query LessonCategoriesBySubject($subjectId: ID!, $mode: LessonVisibilityMode!) {
    lessonCategoriesBySubject(subjectId: $subjectId, mode: $mode) {
      id
      subjectId
      title
      slug
      position
      lessons(mode: $mode) {
        id
        title
        slug
        order
        isPublic
      }
    }
  }
`;

const sortNum3 = (a, b, key) => {
  const avNum = Number(a?.[key]);
  const bvNum = Number(b?.[key]);
  const av = Number.isFinite(avNum) ? avNum : Number.MAX_SAFE_INTEGER;
  const bv = Number.isFinite(bvNum) ? bvNum : Number.MAX_SAFE_INTEGER;
  return av - bv;
};

const SMiddlePageTutor = () => {
  const apollo = useApolloClient();
  const detailRootRef3 = useRef(null);
  const { subjectSlug } = useParams();

  // Sticky footer behavior inside PageWithLayout (.page-content)
  useEffect(() => {
    const applyStickyFooterLayout3 = () => {
      const pageContentEl = document.querySelector(".page-content");
      if (!pageContentEl) return;

      const headerEl =
        document.querySelector("header") ||
        document.querySelector(".header") ||
        document.querySelector(".Header") ||
        document.querySelector("[data-header]");

      const footerEl =
        document.querySelector("footer") ||
        document.querySelector(".footer") ||
        document.querySelector(".Footer") ||
        document.querySelector("[data-footer]");

      const headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
      const footerH = footerEl ? footerEl.getBoundingClientRect().height : 0;

      const minH = Math.max(0, window.innerHeight - headerH - footerH);

      pageContentEl.style.display = "flex";
      pageContentEl.style.flexDirection = "column";
      pageContentEl.style.minHeight = `${minH}px`;
    };

    applyStickyFooterLayout3();
    window.addEventListener("resize", applyStickyFooterLayout3);
    return () => window.removeEventListener("resize", applyStickyFooterLayout3);
  }, []);

  // 1) subject by slug
  const {
    data: subjectsData3,
    loading: subjectsLoading3,
    error: subjectsError3,
  } = useQuery(SUBJECTS_QUERY3, {
    fetchPolicy: "network-only",
  });

  const subject3 = useMemo(() => {
    const list = subjectsData3?.subjects || [];
    return list.find((s) => s?.slug === subjectSlug) || null;
  }, [subjectsData3, subjectSlug]);

  // 2) category name lookup
  const { data: categoriesData3 } = useQuery(FACULTY_CATEGORIES_QUERY3, {
    variables: { facultyId: DEFAULT_FACULTY_ID },
    fetchPolicy: "network-only",
  });

  const [categoryName3, setCategoryName3] = useState("");
  const [categoryLoading3, setCategoryLoading3] = useState(false);

  useEffect(() => {
    const run3 = async () => {
      if (!subject3?.slug) return;
      const cats = categoriesData3?.facultyCategoriesByFaculty || [];
      if (!cats.length) return;

      setCategoryLoading3(true);

      try {
        const results = await Promise.all(
          cats.map((c) =>
            apollo.query({
              query: SUBJECTS_BY_CATEGORY_QUERY3,
              variables: { facultyCategoryId: c.id },
              fetchPolicy: "network-only",
            })
          )
        );

        let foundName = "";
        for (let i = 0; i < results.length; i++) {
          const rel = results[i]?.data?.subjectsByFacultyCategory || [];
          const hit = rel.find((x) => x?.subject?.slug === subject3.slug);
          if (hit) {
            foundName =
              cats.find((c) => c.id === hit.facultyCategory?.id)?.name || "";
            break;
          }
        }

        setCategoryName3(foundName);
      } catch (e) {
        setCategoryName3("");
      } finally {
        setCategoryLoading3(false);
      }
    };

    run3();
  }, [apollo, categoriesData3, subject3]);

  // 3) detail blocks
  const { data: detailData3 } = useQuery(SUBJECT_DETAIL_BLOCKS_QUERY3, {
    variables: { subjectId: subject3?.id },
    skip: !subject3?.id,
    fetchPolicy: "network-only",
  });

  const detailBlocks3 = useMemo(() => {
    const list = detailData3?.subjectDetailBlocks || [];
    return [...list]
      .filter((b) => b?.id)
      .sort((a, b) => {
        const diff = sortNum3(a, b, "position");
        if (diff !== 0) return diff;
        return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
      });
  }, [detailData3]);


  useEffect(() => {
    if (!detailRootRef3.current) return undefined;
    return installSavedFormulaRenderer(detailRootRef3.current);
  }, [detailBlocks3]);

  // 4) lesson categories + lessons (tutor gets ALL)
  const { data: lessonCatData3, loading: lessonCatLoading3 } = useQuery(
    LESSON_CATEGORIES_BY_SUBJECT_QUERY3,
    {
      variables: { subjectId: subject3?.id, mode: "ALL" },
      skip: !subject3?.id,
      fetchPolicy: "network-only",
    }
  );

  const lessonCategories3 = useMemo(() => {
    const cats = lessonCatData3?.lessonCategoriesBySubject || [];
    const sortedCats = [...cats].sort((a, b) => sortNum3(a, b, "position"));

    return sortedCats.map((c) => {
      const lessons = (c.lessons || [])
        .slice()
        .sort((a, b) => sortNum3(a, b, "order"));
      return { ...c, lessons };
    });
  }, [lessonCatData3]);

  if (subjectsLoading3) {
    return (
      <div className="smiddle3-root3">
        <div className="smiddle3-loading3">Lade Fach…</div>
      </div>
    );
  }

  if (subjectsError3) {
    return (
      <div className="smiddle3-root3">
        <div className="smiddle3-errorTitle3">Fehler beim Laden</div>
        <div className="smiddle3-errorText3">{subjectsError3.message}</div>
      </div>
    );
  }

  if (!subject3) {
    return (
      <div className="smiddle3-root3">
        <div className="smiddle3-errorTitle3">Fach nicht gefunden</div>
        <div className="smiddle3-errorText3">
          Slug: <strong>{subjectSlug}</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="smiddle3-page3">
      {/* UPPER PART */}
      <section className="smiddle3-upper3">
        <div className="smiddle3-upperInner3">
          <h1 className="smiddle3-title3">{subject3.title}</h1>

          <div className="smiddle3-category3">
            {categoryLoading3 ? "…" : categoryName3 ? `${categoryName3}` : ""}
          </div>

          <div className="smiddle3-desc3">{subject3.description || ""}</div>

          <div className="smiddle3-actionRow3">
            <Link
              className="smiddle3-mainBtn3"
              to={`/subject/${subjectSlug}/edit`}
            >
              Bearbeiten
            </Link>
          </div>
        </div>
      </section>

      {/* LOWER PART */}
      <section className="smiddle3-lower3">
        <div className="smiddle3-lowerInner3">
          <div className="smiddle3-lowerGrid3">
            {/* LEFT */}
            <div className="smiddle3-left3" ref={detailRootRef3}>
              {detailBlocks3.length > 0 &&
                detailBlocks3.map((b) => (
                  <div
                    key={b.id}
                    className="smiddle3-detailBlock3 lh-lessonContent"
                    dangerouslySetInnerHTML={{ __html: b.html || "" }}
                  />
                ))}
            </div>

            {/* RIGHT */}
            <aside className="smiddle3-rightBox3">
              {lessonCatLoading3 ? (
                <div className="smiddle3-rightLoading3">Lade Inhalte…</div>
              ) : (
                lessonCategories3.map((cat) => (
                  <div key={cat.id} className="smiddle3-rightSection3">
                    <div className="smiddle3-rightTitle3">{cat.title}</div>

                    <div className="smiddle3-timeline3">
                      {cat.lessons.map((l, idx) => {
                        const isLast3 = idx === cat.lessons.length - 1;

                        return (
                          <div key={l.id} className="smiddle3-lessonRow3">
                            <div className="smiddle3-bulletCol3">
                              <span className="smiddle3-bullet3" />
                              {!isLast3 && <span className="smiddle3-line3" />}
                            </div>
                            <Link
                              className="smiddle3-lessonText3"
                              to={`/subject/${subjectSlug}/edit/${l.slug}`}
                            >
                              {l.title}
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </aside>
          </div>
        </div>
      </section>

      <div className="smiddle3-bottomSpace3" />
    </div>
  );
};

export default SMiddlePageTutor;
